import React, { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useLang } from '../../i18n/LanguageContext';
import { PRODUCTS } from '../../data/products';
import { CRM_LOGIN } from '../../data/contact';
import logo from '../../assets/images/logo-kalyani.png';

function LangSwitch({ onDark }) {
  const { lang, setLang } = useLang();
  const base = 'px-2.5 py-1.5 rounded-md transition';
  const on = 'bg-white text-ink shadow-sm';
  const off = onDark ? 'text-white/80 hover:text-white' : 'text-slate-500 hover:text-ink';
  return (
    <div className={`flex p-0.5 rounded-lg text-xs font-bold ${onDark ? 'bg-white/15' : 'bg-slate-100'}`} role="group" aria-label="Language">
      <button type="button" onClick={() => setLang('en')} className={`${base} ${lang === 'en' ? on : off}`} aria-pressed={lang === 'en'}>EN</button>
      <button type="button" onClick={() => setLang('bn')} className={`${base} ${lang === 'bn' ? on : off}`} style={{ fontFamily: "'Hind Siliguri', sans-serif" }} aria-pressed={lang === 'bn'}>বাংলা</button>
    </div>
  );
}

export default function SiteHeader() {
  const { lang, t } = useLang();
  const { pathname } = useLocation();
  const overHero = pathname === '/';
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [insOpen, setInsOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > window.innerHeight * 0.6);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);
  useEffect(() => { setOpen(false); setInsOpen(false); }, [pathname]);

  const transparent = overHero && !scrolled && !open;
  const linkCls = ({ isActive }) => `px-3 py-2 rounded-lg transition ${transparent ? 'hover:bg-white/10' : 'hover:bg-slate-100'} ${isActive ? (transparent ? 'text-amber-200' : 'text-brand') : ''}`;
  const insActive = pathname.startsWith('/services');

  return (
    <header className={`${overHero ? 'fixed' : 'sticky'} top-0 inset-x-0 z-50 transition-colors duration-300 ${transparent ? 'text-white' : 'bg-white text-ink shadow-[0_1px_0_#e2e8f0,0_8px_24px_-16px_rgba(15,27,61,.25)]'}`}>
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-3">
        <Link to="/" className={`shrink-0 rounded-lg ${transparent ? 'bg-white px-1.5 py-1 shadow-sm' : ''}`} aria-label="Kalyani Consultancy home">
          <img src={logo} alt="Kalyani Consultancy" className="h-8 sm:h-9 w-auto" />
        </Link>

        <nav className="hidden lg:flex items-center gap-1 text-[15px] font-medium" aria-label="Main">
          <div className="relative" onMouseEnter={() => setInsOpen(true)} onMouseLeave={() => setInsOpen(false)}>
            <button type="button" onClick={() => setInsOpen((v) => !v)} aria-expanded={insOpen}
              className={`px-3 py-2 rounded-lg transition ${transparent ? 'hover:bg-white/10' : 'hover:bg-slate-100'} ${insActive ? (transparent ? 'text-amber-200' : 'text-brand') : ''}`}>
              {t('navIns')} <span aria-hidden="true" className="text-xs">▾</span>
            </button>
            {insOpen && (
              <div className="absolute left-0 top-full pt-2 w-72">
                <div className="bg-white text-ink rounded-2xl shadow-2xl border border-slate-100 p-2">
                  {PRODUCTS.map((p) => (
                    <Link key={p.id} to={`/services/${p.id}`} className="flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-slate-50">
                      <span className="text-xl">{p.emoji}</span><span className="font-semibold">{p[lang].title}</span>
                    </Link>
                  ))}
                  <Link to="/services" className="block px-3 py-2.5 mt-1 border-t border-slate-100 text-sm font-semibold text-brand">{t('allServices')} →</Link>
                </div>
              </div>
            )}
          </div>
          <NavLink to="/investments" className={linkCls}>{t('navInv')}</NavLink>
          <NavLink to="/calculators" className={linkCls}>{t('navTools')}</NavLink>
          <NavLink to="/about" className={linkCls}>{t('navAbout')}</NavLink>
          <NavLink to="/contact" className={linkCls}>{t('navContact')}</NavLink>
        </nav>

        <div className="flex items-center gap-2">
          <LangSwitch onDark={transparent} />
          <a href={CRM_LOGIN} className={`hidden xl:inline-flex text-sm font-semibold px-3 py-2 rounded-lg ${transparent ? 'hover:bg-white/10' : 'hover:bg-slate-100'}`}>{t('login')}</a>
          <Link to="/pay-premium" className="hidden md:inline-flex text-sm font-bold text-white bg-saffron hover:bg-orange-600 px-4 py-2 rounded-full transition">{t('pay')}</Link>
          <button type="button" onClick={() => setOpen((v) => !v)} aria-expanded={open} aria-label={t('menu')}
            className={`lg:hidden w-10 h-10 rounded-lg flex items-center justify-center ${transparent ? 'bg-white/15' : 'bg-slate-100'}`}>
            <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" aria-hidden="true">
              {open ? <path d="M6 6l12 12M18 6L6 18" /> : <path d="M4 7h16M4 12h16M4 17h16" />}
            </svg>
          </button>
        </div>
      </div>

      {open && (
        <div className="lg:hidden bg-white text-ink border-t border-slate-200 shadow-lg max-h-[calc(100vh-4rem)] overflow-y-auto">
          <nav className="px-4 pb-4 flex flex-col text-[15px] font-medium" aria-label="Mobile">
            <div className="py-3 text-xs font-bold uppercase tracking-wider text-slate-400">{t('navIns')}</div>
            <div className="grid grid-cols-2 gap-2 pb-3 border-b border-slate-100">
              {PRODUCTS.map((p) => (
                <Link key={p.id} to={`/services/${p.id}`} className="flex items-center gap-2 bg-slate-50 rounded-xl px-3 py-2.5 text-sm font-semibold">
                  <span>{p.emoji}</span>{p[lang].title}
                </Link>
              ))}
            </div>
            {[['/investments', 'navInv'], ['/calculators', 'navTools'], ['/about', 'navAbout'], ['/contact', 'navContact'], ['/pay-premium', 'pay']].map(([to, k]) => (
              <Link key={to} to={to} className="py-3 border-b border-slate-100">{t(k)}</Link>
            ))}
            <a href={CRM_LOGIN} className="py-3 text-brand font-semibold">{t('login')} →</a>
          </nav>
        </div>
      )}
    </header>
  );
}
